"use client";

import Image from "next/image";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { localizeValue, normalizeLocale, withLang } from "@/lib/i18n";

interface FooterSettings {
  companyName?: { enUS?: string; ptPT?: string } | null;
  description?: { enUS?: string; ptPT?: string } | null;
  copyright?: { enUS?: string; ptPT?: string } | null;
}

interface SiteFooterProps {
  settings?: FooterSettings | null;
}

const navCopy = {
  "en-US": { shop: "Shop", about: "About", contact: "Contact", profile: "Profile", track: "Track order" },
  "pt-PT": { shop: "Loja", about: "Sobre", contact: "Contacto", profile: "Perfil", track: "Rastrear" },
} as const;

export function SiteFooter({ settings }: SiteFooterProps) {
  const searchParams = useSearchParams();
  const locale = normalizeLocale(searchParams.get("lang"));
  const t = navCopy[locale];

  const description = localizeValue(
    settings?.description,
    locale,
    locale === "pt-PT"
      ? "Lembranças artesanais feitas com dedicação."
      : "Handcrafted gifts made with care.",
  );
  const copyright = localizeValue(
    settings?.copyright,
    locale,
    `© ${new Date().getFullYear()} Linha de Luz`,
  );

  const links = [
    { href: "/shop", label: t.shop },
    { href: "/about", label: t.about },
    { href: "/contact", label: t.contact },
    { href: "/profile", label: t.profile },
    { href: "/track", label: t.track },
  ];

  return (
    <footer className="bg-[#4F4A43]">
      <div className="content-wrap py-10">
        <div className="flex flex-col gap-8 sm:flex-row sm:items-start sm:justify-between">
          {/* Brand */}
          <div className="space-y-2">
            <Image src="/logo-alt.svg" alt="Linha de Luz" width={148} height={54} className="opacity-85" />
            <p className="max-w-55 text-xs leading-5 text-[#9A9088]">{description}</p>
          </div>

          {/* Links */}
          <div className="flex flex-wrap gap-x-8 gap-y-2">
            {links.map((link) => (
              <Link
                key={link.href}
                href={withLang(link.href, locale)}
                className="text-xs tracking-widest uppercase text-[#C8BFB0] transition-colors hover:text-white"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-5">
          <p className="text-[11px] text-[#7A7068]">{copyright}</p>
          <div className="flex items-center gap-4">
            <a
              href="https://www.instagram.com/linhadeluz_store"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="text-[#7A7068] transition hover:text-white"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect width="20" height="20" x="2" y="2" rx="5" ry="5"/>
                <circle cx="12" cy="12" r="4"/>
                <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none"/>
              </svg>
            </a>
            <div className="flex gap-2">
              {(["en-US", "pt-PT"] as const).map((l) => (
                <Link
                  key={l}
                  href={withLang("/", l)}
                  className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold tracking-widest uppercase transition ${
                    locale === l
                      ? "bg-[#F5F2EC] text-[#2B2824]"
                      : "text-[#7A7068] hover:text-white"
                  }`}
                >
                  {l === "en-US" ? "EN" : "PT"}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
